import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle'
import { MatTooltip } from '@angular/material/tooltip'
import {
  ElderFormFieldNoSpinnerDirective,
  ElderMaxValidator,
  ElderMinValidator,
  ElderQuantityInputControlComponent,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { isPackageSharingEnabled } from '../../../../../../environments/environment'
import { ShapePackagingStrategySelectDirective } from '../../../../packages/bundling/shape/shape-packaging-strategy-select.directive'
import { ShapeSelectDirective } from '../../../../packages/bundling/shape/shape-select.directive'
import { ShapeSizeConstraintInputComponent } from '../../../../packages/bundling/shape/shape-size-constraint-input/shape-size-constraint-input.component'
import { StorageNode } from '../../../../storage/nodes/storage-node'
import { StorageNodeSelectDirective } from '../../../../storage/nodes/storage-node-select/storage-node-select.directive'
import { StorageNodeService } from '../../../../storage/nodes/storage-node.service'
import { ItemPackageSharingSelectDirective } from '../../item-package-sharing-select.directive'
import { PackagingDistributionModeSelectDirective } from '../../packaging-distribution-mode-select.directive'
import { PackagingStrategySelectDirective } from '../../packaging-stategy-select.directive'
import { PackageSharingScope, WorkCenter } from '../../work-center'

@Component({
  selector: 'app-work-center-packaging',
  templateUrl: './work-center-packaging.component.html',
  styleUrls: ['./work-center-packaging.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    ElderSelectComponent,
    StorageNodeSelectDirective,
    ShapePackagingStrategySelectDirective,
    FormsModule,
    ShapeSelectDirective,
    MatInput,
    ElderFormFieldNoSpinnerDirective,
    ElderMinValidator,
    ElderMaxValidator,
    ShapeSizeConstraintInputComponent,
    ElderQuantityInputControlComponent,
    MatError,
    ElderValidationErrorDirective,
    MatSlideToggle,
    PackagingStrategySelectDirective,
    PackagingDistributionModeSelectDirective,
    ItemPackageSharingSelectDirective,
    MatTooltip,
    ElderSelectValueDirective,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkCenterPackagingComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _workCenter$: BehaviorSubject<WorkCenter> = new BehaviorSubject<WorkCenter>(null)

  public readonly defaultStorageLocation$ = new BehaviorSubject<StorageNode>(null)

  protected readonly isPackageSharingEnabled = isPackageSharingEnabled

  private storageNodeService = inject(StorageNodeService)

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set workCenter(workCenter: WorkCenter) {
    this._workCenter$.next(workCenter)

    if (workCenter && workCenter.packaging?.defaultStoragePath) {
      this.storageNodeService.findOne(workCenter.packaging.defaultStoragePath).subscribe({
        next: (node) => this.defaultStorageLocation$.next(node),
        error: (err) =>
          this.log.error(
            'Failed to load StorageNode with path ' + workCenter.packaging.defaultStoragePath,
            err
          ),
      })
    }
  }

  public get workCenter(): WorkCenter {
    return this._workCenter$.getValue()
  }

  isShared(wc: WorkCenter): boolean {
    const scope = wc.packaging.packageSharingScope ?? PackageSharingScope.WORK_BATCH
    return scope === PackageSharingScope.WORK_ORDER_ROUTE
  }

  updatePackageSharingScope(toggleChange: MatSlideToggleChange): void {
    if (toggleChange.checked) {
      this.workCenter.packaging.packageSharingScope = PackageSharingScope.WORK_ORDER_ROUTE
    } else {
      this.workCenter.packaging.packageSharingScope = PackageSharingScope.WORK_BATCH
    }
  }
}
